import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API } from '../utils/api';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const lastFetchTime = useRef(0);
  const REFRESH_THROTTLE_MS = 5000; // Minimum 5 seconds between refreshes

  const fetchSubscriptionStatus = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'admin') {
      setLoading(false);
      return;
    }

    // Throttle requests unless forced
    const now = Date.now();
    if (!force && (now - lastFetchTime.current) < REFRESH_THROTTLE_MS) {
      return;
    }
    lastFetchTime.current = now;

    try {
      const response = await axios.get(`${API}/admin/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
      setError(null);
      
      // Store in localStorage for quick access on page reload
      localStorage.setItem('subscriptionStatus', JSON.stringify(response.data));
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch subscription status');
      
      // If we get a 401, the token is invalid - clear auth and redirect to login
      if (err.response?.status === 401) {
        setSubscriptionStatus(null);
        // Clear auth data and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('subscriptionStatus');
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch status on mount
  useEffect(() => {
    fetchSubscriptionStatus(true); // Force fetch on mount
  }, [fetchSubscriptionStatus]);

  // Refresh subscription status on route/navigation changes with debouncing
  useEffect(() => {
    // Skip on initial mount (handled above)
    if (loading) return;
    
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!token || userType !== 'admin') return;

    // Debounce navigation-triggered refreshes to prevent rapid successive calls
    const debounceTimer = setTimeout(() => {
      fetchSubscriptionStatus(false); // Throttled fetch on navigation
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [location.pathname, fetchSubscriptionStatus, loading]);

  // Also check for subscription status in localStorage (set during login)
  useEffect(() => {
    const storedStatus = localStorage.getItem('subscriptionStatus');
    if (storedStatus && !subscriptionStatus) {
      try {
        setSubscriptionStatus(JSON.parse(storedStatus));
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
  }, [subscriptionStatus]);

  // Derived state
  const isRestricted = subscriptionStatus?.access?.subscription_page_only || false;
  const showGraceBanner = subscriptionStatus?.access?.show_grace_banner || false;
  const hasFullAccess = subscriptionStatus?.access?.full_access || false;
  const status = subscriptionStatus?.status || null;
  const daysRemaining = subscriptionStatus?.grace_period?.days_remaining || 0;
  const totalDays = subscriptionStatus?.grace_period?.total_days || 14;
  const subscriptionTier = subscriptionStatus?.subscription_tier || 'Starter';
  const message = subscriptionStatus?.message || '';
  const actionRequired = subscriptionStatus?.action_required || null;
  
  // Pending downgrade state
  const pendingDowngrade = subscriptionStatus?.pending_downgrade || null;
  const hasPendingDowngrade = subscriptionStatus?.pending_downgrade?.active || false;
  
  // Flash upgrade state
  const flashUpgrade = subscriptionStatus?.flash_upgrade || null;
  const hasFlashUpgrade = subscriptionStatus?.flash_upgrade?.active || false;
  const flashUpgradeDaysRemaining = subscriptionStatus?.flash_upgrade?.days_remaining || 0;
  
  // Mid-subscription upgrade state (incomplete upgrade)
  const midSubscriptionUpgrade = subscriptionStatus?.mid_subscription_upgrade || null;
  const hasMidSubscriptionUpgrade = subscriptionStatus?.mid_subscription_upgrade?.active || false;
  
  // Effective plan (accounts for flash upgrade and pending downgrade)
  const effectivePlan = subscriptionStatus?.effective_plan || subscriptionStatus?.subscription_tier || 'Starter';
  const effectivePlanName = subscriptionStatus?.effective_plan_name || subscriptionTier;

  const value = {
    subscriptionStatus,
    loading,
    error,
    refreshStatus: fetchSubscriptionStatus,
    
    // Computed properties for easy access
    isRestricted,
    showGraceBanner,
    hasFullAccess,
    status,
    daysRemaining,
    totalDays,
    subscriptionTier,
    message,
    actionRequired,
    
    // Pending downgrade properties
    pendingDowngrade,
    hasPendingDowngrade,
    effectivePlan,
    effectivePlanName,
    
    // Flash upgrade properties
    flashUpgrade,
    hasFlashUpgrade,
    flashUpgradeDaysRemaining,
    
    // Mid-subscription upgrade properties (incomplete upgrade)
    midSubscriptionUpgrade,
    hasMidSubscriptionUpgrade,
    
    // Helper methods
    isGracePeriod: status === 'GRACE_PERIOD',
    isGraceExpired: status === 'GRACE_EXPIRED',
    isActive: status === 'ACTIVE',
    isSuspended: status === 'SUSPENDED',
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
